import { useEffect, useState } from "react";

// DEPENDENCIES
import QRCode from "qrcode";
import { useLocation } from "react-router-dom";
import {
  DocumentData,
  DocumentReference,
  Firestore,
  getFirestore,
  onSnapshot,
  setDoc,
} from "@firebase/firestore";

// UTILS
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { logAnalyticsAndHotjarEvent } from "../../main";
import {
  onUpdatePrimaryButton,
  onUpdateSecondaryButton,
  onUpdateTertiaryButton,
} from "../../reducers/buttonsReducer";
import { closeModal, openModal } from "../../reducers/modalReducer";
import {
  onUpdateOnClose,
  onUpdateQrImage,
  onUpdateScannedQr,
  onUpdateSuccessMessage,
} from "../../reducers/qrReducer";
import useForm from "../../hooks/useForm";
import useWindowWidth from "../../hooks/useWindowWidth";
import { showToastMessage } from "@almafintech/react-components/ToastMessage";
import useRemoteParams from "../../hooks/useRemoteParams";

// TYPES
import { ComponentProps, DniImageType, FirebaseDniData } from "../../types";

// COMPONENTS
import { LoadingCircle } from "@almafintech/react-components/LoadingCircle";
import { Button } from "@almafintech/react-components/Button";
import { InputCopy } from "@almafintech/react-components/InputCopy";
import Layout from "../Layout/Layout";
import FormContainer from "../FormContainer/FormContainer";
import Container from "../Container/Container";
import UploadDniForm from "./UploadDniForm";
import QRModal from "../QRModal/QRModal";

// ICONS
import successIcon from "../../assets/images/close-window.svg";

// STYLES
import styles from "./UploadDniCard.module.scss";
import { qrStyle } from "../../utils";

interface UploadDniCardProps extends ComponentProps {
  getDniRef: ({
    db,
    uuid,
    email,
  }: {
    db: Firestore;
    uuid: string;
    email: string;
  }) => DocumentReference<DocumentData, DocumentData>;
  onUploadDni: (
    type: DniImageType,
    file: File,
    uuid: string,
    cuit: string
  ) => Promise<void>;
  showCopyLink?: boolean;
}

const UploadDniCard = ({
  formik,
  getDniRef,
  showCopyLink,
  onUploadDni,
}: UploadDniCardProps) => {
  const {
    successContainer,
    loading,
    secondaryButton,
    confirmButton,
    layoutContainer,
    buttonsContainer,
    copyLinkContainer,
  } = styles;

  const [success, setSuccess] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  const [dniData, setDniData] = useState<FirebaseDniData>({
    scannedQR: false,
    frontDNI: "",
    backDNI: "",
  });

  const [isLoadingData, setIsLoadingData] = useState<boolean>(true);
  const [isLoadingButton, setIsLoadingButton] = useState<boolean>(false);

  const dispatch = useAppDispatch();
  const { icon } = useAppSelector((state) => state.activeTemplate);

  const dimensions = useWindowWidth();
  const isMobile = dimensions.width < 768;

  const { handleBack, handleContinue } = useForm(formik);

  // Remote
  const location = useLocation();
  const isRemoteStep = location.pathname.includes("dni");

  const { remoteUrlParams, values } = useRemoteParams();
  const { continueWithQR, uuid, email, cuit } = values;

  const dniLink = `${window.location.origin}/dni?${remoteUrlParams.toString()}`;

  // Firestore
  const db = getFirestore();
  const dniRef = getDniRef({ db, uuid: uuid, email: email });

  const disabledExternalLinkButton =
    !dniData.frontDNI || !dniData.backDNI || error;

  const disablePrimaryButton = dniData.scannedQR || disabledExternalLinkButton;

  const handleUpload = () => {
    setSuccess(true);
    if (continueWithQR) setDoc(dniRef, { scannedQR: false }, { merge: true });
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(dniLink);
    showToastMessage("Link copiado al portapapeles.", {
      containerId: "layoutPage",
      type: "success",
    });
  };

  const handleOpenQr = () => {
    dispatch(onUpdateScannedQr(false));
    dispatch(openModal({ name: "modal-qr" }));
  };

  const dniStep = {
    name: "Identidad",
    subtitle: "Cargá fotos del frente y dorso de tu DNI.",
    step: 1,
  };

  const UploadDNI = () => (
    <UploadDniForm
      dniData={dniData}
      dniRef={dniRef}
      onUploadDni={onUploadDni}
      setError={setError}
      setIsLoadingButton={setIsLoadingButton}
      isExternalLink={isRemoteStep}
      isMobile={isMobile}
      success={success}
      uuid={uuid}
      cuit={cuit}
    />
  );

  useEffect(() => {
    const unsub = onSnapshot(dniRef, (document) => {
      if (document.exists()) {
        const data = document.data() as FirebaseDniData;
        setDniData((prevState) => ({
          ...prevState,
          ...data,
        }));
        setIsLoadingData(false);
      } else {
        setDoc(dniRef, dniData, { merge: true });
      }
    });

    return unsub;
  }, []);

  // Set QR information
  useEffect(() => {
    QRCode.toDataURL(dniLink.concat(`&withQR=true`), qrStyle).then((img) =>
      dispatch(onUpdateQrImage(img))
    );

    dispatch(
      onUpdateSuccessMessage("Ya podés cargar las fotos desde tu teléfono.")
    );
    dispatch(
      onUpdateOnClose(() => {
        setDoc(dniRef, { scannedQR: false }, { merge: true });
      })
    );
  }, []);

  // Update QR modal
  useEffect(() => {
    if (continueWithQR && !isLoadingData) {
      logAnalyticsAndHotjarEvent("qr_code_scanned_dni");
      setDoc(dniRef, { scannedQR: true }, { merge: true });
      localStorage.clear();
    }
  }, [continueWithQR, isLoadingData]);

  useEffect(() => {
    dniData.scannedQR && dispatch(onUpdateScannedQr(true));
  }, [dniData]);

  // Close modal when all files were uploaded
  useEffect(() => {
    if (!disablePrimaryButton) {
      dispatch(closeModal());
    }
  }, [dniData, error]);

  // Manage buttons
  useEffect(() => {
    if (!isMobile) {
      dispatch(
        onUpdateSecondaryButton({
          text: "Cargar con mi teléfono",
          disabled: false,
          isLoading: false,
          onClick: handleOpenQr,
          show: true,
        })
      );

      dispatch(
        onUpdateTertiaryButton({
          text: "Volver",
          disabled: false,
          isLoading: false,
          onClick: handleBack,
          show: true,
        })
      );
    } else {
      dispatch(
        onUpdateSecondaryButton({
          text: "Volver",
          disabled: false,
          isLoading: false,
          onClick: handleBack,
          show: true,
        })
      );

      dispatch(
        onUpdateTertiaryButton({
          show: false,
        })
      );
    }

    dispatch(
      onUpdatePrimaryButton({
        text: "Continuar",
        disabled: disablePrimaryButton,
        isLoading: isLoadingButton,
        onClick: handleContinue,
        show: true,
      })
    );
  }, [dniData, isLoadingButton, isMobile]);

  return (
    <section>
      <QRModal />
      {success && isRemoteStep && continueWithQR && (
        <div className={successContainer}>
          <img src={successIcon} alt="successQRIcon" />
          <h2>¡Listo! Podés cerrar esta página y seguir en tu computadora.</h2>
        </div>
      )}
      {(!success || !continueWithQR) &&
        (isLoadingData ? (
          <div className={loading}>
            <LoadingCircle width="50" />
          </div>
        ) : isRemoteStep ? (
          <Layout hideStepper>
            <Container className={layoutContainer}>
              <FormContainer
                icon={icon}
                currentStep={dniStep}
                currentSubStep={1}
              >
                <UploadDNI />
              </FormContainer>
            </Container>
            {!success && (
              <div
                className={`${buttonsContainer} flex gap-2 w-full justify-center`}
              >
                {!isMobile && (
                  <Button
                    variant="secondary"
                    text="Cargar con mi teléfono"
                    className={secondaryButton}
                    onClick={handleOpenQr}
                  />
                )}
                <Button
                  text={continueWithQR ? "Confirmar" : "Continuar"}
                  className={isMobile ? confirmButton : ""}
                  fullWidth={isMobile}
                  size={isMobile ? "mobile" : "desktop"}
                  disabled={disabledExternalLinkButton}
                  isLoading={isLoadingButton}
                  onClick={handleUpload}
                />
              </div>
            )}
          </Layout>
        ) : (
          <>
            <UploadDNI />
            {showCopyLink && (
              <div className={copyLinkContainer}>
                <h4>
                  O compartí el siguiente link y esperá a que carguen las fotos.
                </h4>
                <InputCopy value={dniLink} onClick={handleCopyLink} />
              </div>
            )}
          </>
        ))}
    </section>
  );
};

export default UploadDniCard;
